import { useEffect, useRef, useState } from 'react';
import type { Facility } from '../lib/types';

const RECENTS_KEY = 'facilitywise.recents';
const RECENTS_MAX = 8;

export function pushRecent(id: string): void {
  try {
    const raw = sessionStorage.getItem(RECENTS_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    const next = [id, ...list.filter((x) => x !== id)].slice(0, RECENTS_MAX);
    sessionStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* storage disabled — recents degrade to empty, nothing else breaks */
  }
}

function readRecents(): string[] {
  try {
    const raw = sessionStorage.getItem(RECENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * The facility switcher — what replaces the permanent left rail.
 *
 * The rail cost 296px permanently, showed 3 fields of 40, and could not sort,
 * filter, or answer "every site with a manager vacancy". This gives the same
 * lateral movement at zero permanent cost: click the name or press ⌘K.
 *
 * When not searching it shows recently viewed plus the other facilities in the
 * same district manager's district — the set you actually move between.
 */
export function FacilitySwitcher({
  current,
  facilities,
  onPick,
  onClose,
  onBrowseAll,
}: {
  current: Facility;
  facilities: Facility[];
  onPick: (f: Facility) => void;
  onClose: () => void;
  onBrowseAll: () => void;
}) {
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!popRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Deferred so the click that opened the popover does not immediately close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const term = q.trim().toLowerCase();
  const byId = new Map(facilities.map((f) => [f.id, f]));

  const matches = term
    ? facilities.filter((f) =>
        [f.name, f.code, f.city, f.brand].some((v) => (v || '').toLowerCase().includes(term)),
      )
    : [];

  const recents = readRecents()
    .map((id) => byId.get(id))
    .filter((f): f is Facility => !!f && f.id !== current.id)
    .slice(0, 4);

  const near = current.district
    ? facilities.filter((f) => f.id !== current.id && f.district === current.district)
    : [];

  const group = (label: string, items: Facility[]) =>
    items.length ? (
      <div key={label}>
        <div className="sw-g">{label}</div>
        {items.map((f) => (
          <button
            className={`sw-i${f.id === current.id ? ' on' : ''}`}
            key={f.id}
            onClick={() => onPick(f)}
            type="button"
          >
            <span className={`sw-c${f.active ? '' : ' off'}`}>{f.code}</span>
            <span className="sw-b">
              <span className="sw-n">{f.name}</span>
              <span className="sw-s">
                {[f.city, f.state].filter(Boolean).join(', ')} · {f.brand ?? 'No brand'}
              </span>
            </span>
          </button>
        ))}
      </div>
    ) : null;

  const body = term
    ? group(`${matches.length} match${matches.length === 1 ? '' : 'es'}`, matches.slice(0, 40))
    : [
        group('Recently viewed', recents),
        group(
          current.district ? `Also in ${current.district}'s district` : 'All facilities',
          (near.length ? near : facilities.filter((f) => f.id !== current.id)).slice(0, 8),
        ),
      ];

  const empty = term ? matches.length === 0 : facilities.length <= 1;

  return (
    <div className="sw-pop" ref={popRef} role="dialog" aria-label="Jump to facility">
      <div className="sw-search">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Jump to facility…"
          aria-label="Jump to facility"
        />
      </div>
      <div className="sw-list">{empty ? <div className="sw-empty">No match</div> : body}</div>
      <button className="sw-all" onClick={onBrowseAll} type="button">
        Browse full portfolio →
      </button>
    </div>
  );
}
